import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiRefreshCw, FiDownload, FiChevronRight, FiVolume2 } from 'react-icons/fi';
import Sidebar from '../components/Sidebar';
import DashboardHeader from '../components/DashboardHeader';
import { useSidebar } from '../contexts/SidebarContext';
import { authAPI, mockSubmissionsAPI, recordingsAPI, MockResult, MockGradeByAudioRow } from '../services/api';
import { downloadMockResultBarChartPng, downloadMockResultSegmentsBarChartPng } from '../utils/mockResultChartExport';
import { getOrCreateMockDialogueLabel } from '../utils/mockDialogueCodes';
import { MOCK_GRADING_CRITERIA } from '../constants/mockCriteria';
import { createMockResultBlobCache } from '../utils/mockResultAudioBlobCache';
import '../components/Dashboard.css';
import './MockResultPage.css';

interface MockResultPageProps {
  onLogout?: () => void;
  userEmail?: string;
}

function criterionTone(score: number): 'weak' | 'mid' | 'strong' {
  if (score <= 2) return 'weak';
  if (score >= 4) return 'strong';
  return 'mid';
}

function readCriterionScore(row: MockGradeByAudioRow, key: string): number {
  const v = (row as unknown as Record<string, unknown>)[key];
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n) || n < 1 || n > 5) return 0;
  return n;
}

/** Loads the learner's own recording when the per-recording panel is open (blob URL revoked on unmount). */
function normalizeRecordingId(id: number | string | undefined | null): number | undefined {
  if (id == null) return undefined;
  const n = typeof id === 'number' ? id : Number(id);
  return Number.isFinite(n) && n > 0 ? n : undefined;
}

function normalizeSegmentOrder(v: unknown): number | undefined {
  if (v == null) return undefined;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) && n >= 0 ? Math.trunc(n) : undefined;
}

function readRecordingIdField(obj: Record<string, unknown>, ...keys: string[]): number | undefined {
  for (const k of keys) {
    const v = obj[k];
    const n = normalizeRecordingId(v as number | string | undefined | null);
    if (n != null) return n;
  }
  return undefined;
}

function recordingIdFromGradeRow(
  row: MockGradeByAudioRow,
  byOrder: Map<number, number>,
  parallelIds?: (number | null)[] | undefined,
  index?: number
): number | undefined {
  const o = row as unknown as Record<string, unknown>;
  const fromParallel =
    typeof index === 'number' && parallelIds && parallelIds[index] != null
      ? normalizeRecordingId(parallelIds[index] as number)
      : undefined;
  return (
    readRecordingIdField(o, 'user_recording_id', 'userRecordingId', 'rec_id') ??
    fromParallel ??
    normalizeRecordingId(byOrder.get(row.order))
  );
}

const MockResultSegmentUserAudio: React.FC<{
  audioId?: number | string | null;
  shouldLoad: boolean;
  getOrFetchBlob: (id: number) => Promise<string>;
}> = ({ audioId, shouldLoad, getOrFetchBlob }) => {
  const resolvedId = normalizeRecordingId(audioId ?? undefined);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (!shouldLoad || !resolvedId) {
      setInView(false);
      setSrc(null);
      setErr(null);
      setLoading(false);
      return undefined;
    }
    setInView(false);
    setSrc(null);
    setErr(null);
    setLoading(false);

    const el = wrapRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setInView(true);
          io.disconnect();
        }
      },
      { root: null, rootMargin: '120px', threshold: 0.02 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [shouldLoad, resolvedId]);

  useEffect(() => {
    if (!shouldLoad || !resolvedId || !inView) return undefined;
    let cancelled = false;
    setLoading(true);
    setErr(null);
    getOrFetchBlob(resolvedId)
      .then((u) => {
        if (!cancelled) setSrc(u);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setErr(e instanceof Error ? e.message : 'Could not load recording');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [shouldLoad, resolvedId, inView, getOrFetchBlob]);

  if (!resolvedId) {
    return <span className="mock-result-recording-unavailable">Recording not linked for this segment.</span>;
  }

  const idle = shouldLoad && !inView && !loading && !err && !src;

  if (idle) {
    return (
      <div ref={wrapRef} className="mock-result-audio-shell mock-result-audio-shell--idle">
        <div className="mock-result-audio-shell-head">
          <span className="mock-result-audio-shell-icon" aria-hidden>
            {React.createElement(FiVolume2 as React.ComponentType<{ size?: number }>, { size: 16 })}
          </span>
          <span className="mock-result-audio-shell-title">Your recording</span>
        </div>
        <p className="mock-result-audio-idle-hint">Scroll to this block — audio loads once when visible (cached for this visit).</p>
      </div>
    );
  }

  const awaitingBlob = inView && !src && !err;

  if (loading || awaitingBlob) {
    return (
      <div ref={wrapRef} className="mock-result-audio-shell mock-result-audio-shell--loading" aria-busy="true">
        <div className="mock-result-audio-shell-head">
          <span className="mock-result-audio-shell-icon" aria-hidden>
            {React.createElement(FiVolume2 as React.ComponentType<{ size?: number }>, { size: 16 })}
          </span>
          <span className="mock-result-audio-shell-title">Your recording</span>
        </div>
        <div className="mock-result-audio-skeleton" />
        <p className="mock-result-audio-loading-text">Loading audio…</p>
      </div>
    );
  }
  if (err) {
    return (
      <div ref={wrapRef} className="mock-result-audio-shell mock-result-audio-shell--error" role="alert">
        <div className="mock-result-audio-shell-head">
          <span className="mock-result-audio-shell-icon" aria-hidden>
            {React.createElement(FiVolume2 as React.ComponentType<{ size?: number }>, { size: 16 })}
          </span>
          <span className="mock-result-audio-shell-title">Your recording</span>
        </div>
        <p className="mock-result-audio-err">{err}</p>
      </div>
    );
  }
  return (
    <div ref={wrapRef} className="mock-result-audio-shell">
      <div className="mock-result-audio-shell-head">
        <span className="mock-result-audio-shell-icon" aria-hidden>
          {React.createElement(FiVolume2 as React.ComponentType<{ size?: number }>, { size: 16 })}
        </span>
        <span className="mock-result-audio-shell-title">Your recording</span>
      </div>
      <div className="mock-result-audio-el-wrap">
        <audio className="mock-result-audio-el" controls src={src ?? undefined} preload="metadata" />
      </div>
    </div>
  );
};

const MockResultPage: React.FC<MockResultPageProps> = ({ onLogout, userEmail }) => {
  const { submissionId } = useParams<{ submissionId: string }>();
  const navigate = useNavigate();
  const { isCollapsed } = useSidebar();
  const [userInfo, setUserInfo] = useState({
    email: userEmail || 'anonymous.user@naati.edu',
    organization: 'NAATI',
    first_name: '',
  });
  const [result, setResult] = useState<MockResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chartExporting, setChartExporting] = useState(false);
  const [segmentChartExporting, setSegmentChartExporting] = useState(false);
  const [recordingsSectionOpen, setRecordingsSectionOpen] = useState(false);

  const chartBusy = chartExporting || segmentChartExporting;

  const audioBlobCacheRef = useRef<ReturnType<typeof createMockResultBlobCache> | null>(null);

  useEffect(() => {
    audioBlobCacheRef.current?.revokeAll();
    audioBlobCacheRef.current = null;
  }, [submissionId]);

  const getOrFetchBlob = useCallback((id: number) => {
    if (!audioBlobCacheRef.current) {
      audioBlobCacheRef.current = createMockResultBlobCache((aid) => recordingsAPI.fetchAudioBlob(aid));
    }
    return audioBlobCacheRef.current.getOrFetch(id);
  }, []);

  useEffect(() => {
    if (submissionId) {
      fetchResult();
    }
  }, [submissionId]);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const profile = await authAPI.getProfile();
        setUserInfo({
          email: profile.email || userEmail || 'anonymous.user@naati.edu',
          organization: profile.organization || 'NAATI',
          first_name: profile.first_name || '',
        });
      } catch {
        // keep defaults
      }
    };
    loadProfile();
  }, [userEmail]);

  const fetchResult = async () => {
    if (!submissionId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await mockSubmissionsAPI.getResult(Number(submissionId));
      setResult(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load result');
      setResult(null);
    } finally {
      setLoading(false);
    }
  };

  const mockDialogueLabel = useMemo(() => {
    if (result?.dialogue_id != null && Number.isFinite(result.dialogue_id)) {
      return getOrCreateMockDialogueLabel(result.dialogue_id);
    }
    return 'Mock test';
  }, [result?.dialogue_id]);

  const sortedAudioGrades = useMemo(() => {
    const g = result?.grades_by_audio;
    if (!g?.length) return [];
    return [...g].sort((a, b) => a.order - b.order);
  }, [result?.grades_by_audio]);

  const recordingIdByOrder = useMemo(() => {
    const m = new Map<number, number>();
    for (const x of result?.user_recordings_by_order ?? []) {
      const o = x as unknown as Record<string, unknown>;
      const ord = normalizeSegmentOrder(o.order);
      const rid = readRecordingIdField(o, 'user_recording_id', 'userRecordingId');
      if (ord != null && rid != null) {
        m.set(ord, rid);
      }
    }
    return m;
  }, [result?.user_recordings_by_order]);

  const handleDownloadPerformanceChart = useCallback(() => {
    if (!result || result.status !== 'graded' || chartBusy) return;
    const criteria = result.criteria ?? [];
    if (!criteria.length) return;
    const sid = result.id ?? Number(submissionId);
    if (!Number.isFinite(sid)) return;

    setChartExporting(true);
    downloadMockResultBarChartPng({
      criteria: criteria.map((c) => ({ label: c.label, score: c.score })),
      dialogueName:
        result.dialogue_id != null && Number.isFinite(result.dialogue_id)
          ? getOrCreateMockDialogueLabel(result.dialogue_id)
          : 'Mock test',
      categoryName: undefined,
      overallScore: result.overall_score ?? 0,
      submissionId: sid,
      submittedAt: result.created_at,
      onComplete: () => setChartExporting(false),
    });
  }, [result, submissionId, chartBusy]);

  const handleDownloadSegmentChart = useCallback(() => {
    if (!result || result.status !== 'graded' || chartBusy) return;
    if (!sortedAudioGrades.length) return;
    const sid = result.id ?? Number(submissionId);
    if (!Number.isFinite(sid)) return;

    setSegmentChartExporting(true);
    downloadMockResultSegmentsBarChartPng({
      dialogueName: mockDialogueLabel,
      segments: sortedAudioGrades.map((row) => ({
        title: `Recording ${row.order + 1}`,
        criteria: MOCK_GRADING_CRITERIA.map(({ key, label }) => ({
          label,
          score: readCriterionScore(row, key),
        })),
      })),
      submissionId: sid,
      submittedAt: result.created_at,
      onComplete: () => setSegmentChartExporting(false),
    });
  }, [result, submissionId, chartBusy, sortedAudioGrades, mockDialogueLabel]);

  return (
    <div className={`dashboard ${isCollapsed ? 'sidebar-collapsed' : ''}`}>
      <Sidebar />
      <div className="dashboard-main">
        <DashboardHeader
          userEmail={userInfo.email}
          organization={userInfo.organization}
          onLogout={onLogout}
        />
        <div className="dashboard-content">
          <div className="dashboard-content-inner">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="mock-result-content"
            >
              <button type="button" className="mock-result-back" onClick={() => navigate('/mock-service')}>
                {React.createElement(FiArrowLeft as React.ComponentType<{ size?: number }>, { size: 18 })}
                Back to Mock Service
              </button>

              {loading ? (
                <div className="mock-result-loading">
                  {React.createElement(FiRefreshCw as React.ComponentType<{ className?: string }>, { className: 'spinner' })}
                  <p>Loading result...</p>
                </div>
              ) : error ? (
                <div className="mock-result-error">
                  <p>{error}</p>
                  <button onClick={fetchResult}>Retry</button>
                </div>
              ) : !result ? (
                <div className="mock-result-error">
                  <p>Result not found</p>
                  <button onClick={() => navigate('/mock-service')}>Back to Mock Service</button>
                </div>
              ) : result.status !== 'graded' ? (
                <div className="mock-result-pending">
                  <header className="mock-result-page-heading mock-result-page-heading-pending">
                    <h1 className="mock-result-title">Mock test result</h1>
                    <div className="mock-result-dialogue-code-block" aria-label="Dialogue code">
                      <span className="mock-result-dialogue-code-label">Dialogue code</span>
                      <span className="mock-result-dialogue-code-value">{mockDialogueLabel}</span>
                    </div>
                  </header>
                  <p>{result.message || 'Results are not yet available. Please wait for grading.'}</p>
                </div>
              ) : (
                <>
                  <header className="mock-result-page-heading">
                    <h1 className="mock-result-title">Mock test result</h1>
                    <div className="mock-result-dialogue-code-block" aria-label="Dialogue code">
                      <span className="mock-result-dialogue-code-label">Dialogue code</span>
                      <span className="mock-result-dialogue-code-value">{mockDialogueLabel}</span>
                    </div>
                  </header>

                  {result.admin_overall_feedback?.trim() ? (
                    <div className="mock-result-admin-feedback">
                      <h2 className="mock-result-admin-feedback-title">Feedback from your assessor</h2>
                      <p className="mock-result-admin-feedback-body">{result.admin_overall_feedback.trim()}</p>
                    </div>
                  ) : null}

                  <p className="mock-result-scale-help">
                    Each criterion is scored from <strong>1</strong> (needs improvement) to <strong>5</strong> (strong).
                    Lower scores point to areas to focus on; the breakdown below shows exactly how each of your
                    recordings was rated.
                  </p>

                  {result.grading_complete === false && (
                    <p className="mock-result-partial-warning" role="status">
                      Some audio items have no grade on file; scores reflect graded items only. Contact support if this
                      looks wrong.
                    </p>
                  )}

                  <div className="mock-result-overall">
                    <span className="mock-result-overall-label">Overall Score</span>
                    <span className="mock-result-overall-value">{(result.overall_score ?? 0).toFixed(2)}</span>
                    <span className="mock-result-overall-max">/ 5.00</span>
                  </div>

                  <div className="mock-result-chart-section">
                    <div className="mock-result-chart-section-header">
                      <h2>Overall — average by criterion</h2>
                      <div className="mock-result-chart-actions">
                        <button
                          type="button"
                          className="mock-result-download-chart-btn"
                          onClick={handleDownloadPerformanceChart}
                          disabled={chartBusy || !(result.criteria && result.criteria.length)}
                          aria-busy={chartExporting}
                          aria-label="Download overall criterion bar chart as PNG"
                        >
                          {React.createElement(FiDownload as React.ComponentType<{ size?: number }>, { size: 18 })}
                          {chartExporting ? 'Preparing…' : 'Overall chart (PNG)'}
                        </button>
                        <button
                          type="button"
                          className="mock-result-download-chart-btn mock-result-download-chart-btn-secondary"
                          onClick={handleDownloadSegmentChart}
                          disabled={chartBusy || !sortedAudioGrades.length}
                          aria-busy={segmentChartExporting}
                          aria-label="Download per-recording bar chart as PNG"
                        >
                          {React.createElement(FiDownload as React.ComponentType<{ size?: number }>, { size: 18 })}
                          {segmentChartExporting ? 'Preparing…' : 'By recording (PNG)'}
                        </button>
                      </div>
                    </div>
                    <div className="mock-result-bar-chart">
                      {(result.criteria ?? []).map((c) => (
                        <div key={c.key} className="mock-result-bar-row">
                          <label>{c.label}</label>
                          <div className="mock-result-bar-track">
                            <div
                              className="mock-result-bar-fill"
                              style={{ width: `${(c.score / 5) * 100}%` }}
                            />
                          </div>
                          <span className="mock-result-bar-value">{c.score.toFixed(2)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {sortedAudioGrades.length > 0 ? (
                    <section className="mock-result-by-recording" aria-label="Your recordings, scores for each part">
                      <button
                        type="button"
                        className="mock-result-recordings-toggle"
                        aria-expanded={recordingsSectionOpen}
                        onClick={() => setRecordingsSectionOpen((o) => !o)}
                      >
                        <span className="mock-result-recordings-chevron" aria-hidden>
                          {React.createElement(FiChevronRight as React.ComponentType<{ size?: number }>, { size: 20 })}
                        </span>
                        <span>Your recordings — scores for each part</span>
                      </button>
                      {recordingsSectionOpen ? (
                        <div className="mock-result-by-recording-panel">
                          <p className="mock-result-by-recording-intro">
                            Each block matches one segment of the test in order. Audio loads when you scroll to it
                            (one request per clip per visit; reopening this panel uses cache — no repeat downloads).
                          </p>
                          {sortedAudioGrades.map((row, idx) => (
                            <div key={row.order} className="mock-result-segment-card">
                              <h3 className="mock-result-segment-heading">Recording {row.order + 1}</h3>
                              <div className="mock-result-segment-audio-row">
                                <MockResultSegmentUserAudio
                                  audioId={recordingIdFromGradeRow(
                                    row,
                                    recordingIdByOrder,
                                    result.learner_recording_ids,
                                    idx
                                  )}
                                  shouldLoad={recordingsSectionOpen}
                                  getOrFetchBlob={getOrFetchBlob}
                                />
                              </div>
                              <ul className="mock-result-criterion-list">
                                {MOCK_GRADING_CRITERIA.map(({ key, label }) => {
                                  const score = readCriterionScore(row, key);
                                  const tone = criterionTone(score);
                                  return (
                                    <li key={key} className={`mock-result-criterion-item mock-result-criterion-${tone}`}>
                                      <span className="mock-result-criterion-label">{label}</span>
                                      <span className="mock-result-criterion-bar-wrap">
                                        <span
                                          className="mock-result-criterion-bar"
                                          style={{ width: `${(score / 5) * 100}%` }}
                                        />
                                      </span>
                                      <span className="mock-result-criterion-score">{score}</span>
                                      {tone === 'weak' ? (
                                        <span className="mock-result-criterion-hint">Priority area</span>
                                      ) : null}
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </section>
                  ) : null}

                  <div className="mock-result-date">
                    Submitted: {new Date(result.created_at ?? '').toLocaleDateString()}
                  </div>
                </>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MockResultPage;
